import { Listing, PaymentTerms, TRACK_DOCUMENT_TYPES } from "../../../../types";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import z from "zod";
import { RentalListingSchema } from "@/modules/property-tracks/utils/listing";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ListingType } from "@/modules/property-tracks/dtos";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import RentalListingForm from "./form";
import { useEditListing } from "@/modules/property-tracks/hooks/listings/edit-listing.hook";
import { enforceMandates, MandateFields } from "@/modules/common/utils/zod";
import { useTrackDocumentQuery } from "@/modules/property-tracks/hooks/track-document.hook";
import ImageSelector from "@/modules/common/components/input/image-selector";
import { Box, FormHelperText, Typography } from "@mui/material";

type ListingFormFields = z.infer<ReturnType<typeof RentalListingSchema>>;

export default function EditRentalListingModal({
  listing,
  trackUid,
  propertyUid,
  open,
  handleOpen,
  handleClose,
  manddateFields,
}: {
  listing: Listing;
  trackUid?: string;
  propertyUid?: string;
  open: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  manddateFields?: MandateFields<ListingFormFields>;
}) {
  const defaultValues: ListingFormFields = {
    documentUids: listing.trackDocuments
      ? listing.trackDocuments.map((td) => td.uid)
      : undefined,
    isRented: listing.isRented ?? false,
    propertyRental: {
      availableFrom: listing.propertyRental.availableFrom ?? undefined,
      annualIncreasePercent:
        listing.propertyRental.annualIncreasePercent ?? undefined,
      furnished: listing.propertyRental.furnished ?? undefined,
      insuranceDepositRequired:
        listing.propertyRental.insuranceDepositRequired ?? undefined,
      minimumStayMonths: listing.propertyRental.minimumStayMonths ?? undefined,
      noBrokerCommission: !listing.propertyRental.noBrokerCommission,
      rentalPrice: listing.propertyRental.rentalPrice ?? undefined,
      showingFrom: listing.propertyRental.showingFrom ?? undefined,
      paymentTerms: listing.propertyRental.paymentTerms
        ? {
            value: listing.propertyRental.paymentTerms,
            label: PaymentTerms[listing.propertyRental.paymentTerms],
          }
        : undefined,
    },
  };

  const { data: images, isLoading: imagesLoading } = useTrackDocumentQuery({
    uid: trackUid,
    documentType: TRACK_DOCUMENT_TYPES.Media,
  });

  const { handleSubmit, reset, control } = useForm<ListingFormFields>({
    resolver: zodResolver(
      RentalListingSchema().superRefine((data, ctx) => {
        if (manddateFields) enforceMandates(data, manddateFields, ctx, []);
      })
    ),
    values: defaultValues,
  });

  const onSubmit: SubmitHandler<ListingFormFields> = async (data) => {
    submitAddRentalListing(data);
  };

  const mutation = useEditListing();

  const submitAddRentalListing = (data: ListingFormFields) => {
    mutation.mutate(
      {
        uid: listing.uid,
        data: {
          listingType: ListingType.RENTAL,
          documentUids: data.documentUids,
          isRented: data.isRented,
          propertyRental: {
            ...data.propertyRental,
            paymentTerms: data.propertyRental?.paymentTerms?.value,
            noBrokerCommission: !data.propertyRental?.noBrokerCommission,
          },
        },
      },
      {
        onSuccess: () => {
          handleModalClose();
          reset();
          showToast({
            type: ToastType.SUCCESS,
            message: "Listing updated successfully",
            toastId: TOAST_IDS.EDIT_LISTING_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.EDIT_LISTING_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      }
    );
  };

  const handleModalClose = () => {
    handleClose();
    reset();
  };

  const handleModalOpen = () => {
    handleOpen();
    reset(defaultValues);
  };

  return (
    <EditFieldModal
      open={open}
      setOpen={handleModalOpen}
      header="Edit listing"
      width={580}
      handleConfirm={handleSubmit(onSubmit)}
      handleCloseModal={handleModalClose}
      isLoading={mutation.isPending}
    >
      <RentalListingForm
        control={control}
        namePrefix="propertyRental"
        propertyUid={propertyUid}
      />
      <Controller
        control={control}
        name={"documentUids"}
        render={({ field, fieldState }) => {
          return (
            <Box>
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
              >
                Listing Media
              </Typography>
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.xs.regular}
              >
                Select photos
              </Typography>

              <ImageSelector
                images={images ?? []}
                loading={imagesLoading}
                selectedImagesIds={field.value ?? []}
                setSelectedImagesIds={(ids: string[]) => field.onChange(ids)}
              />
              {fieldState.error && (
                <FormHelperText error>{fieldState.error.message}</FormHelperText>
              )}
            </Box>
          );
        }}
      ></Controller>
    </EditFieldModal>
  );
}
